import {
    useMemo,
} from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { memberIdSelector } from 'redux-store/auth/authSelectors';
import { withCSContext } from 'services/ApolloClient';
import { useFormatting } from 'locale';
import { useSchema } from 'domain/ClientService';
import { adaptClientInformation } from '../adaptors/adaptClientInfo';

const CLIENT_INFORMATION = gql`
    query ClientInformation($clientId: Int!) {
        contacts(
            where: { id: { eq: $clientId } }
        ){
            items {
                id
                personalInformation {
                    firstName
                    lastName
                    displayName
                    dateOfBirth
                    primaryEmail
                    primaryPhoneNumber
                    primaryAddressCountry { label value }
                    primaryAddressLine1
                    primaryAddressLine2
                    primaryAddressStreet
                    primaryAddressPostalCode
                    primaryAddressCity
                    extDateOfDeath
                }
                relationshipInformation {
                    isProspect
                    externalId
                }
                investmentInformation {
                    chosenRiskCategory { label value }
                }
                taxSituation {
                    isUsPerson
                    isSubjectToTaxInAnotherCountry
                    isUsCitizenOrSubjectToUsTax
                },
            }
        }
    }
`;

export const useGetClientInfo = (options) => {
    const { clientId } = options;
    const { t } = useTranslation();
    const memberId = useSelector(memberIdSelector);
    const { getFormattedDate, getFormattedPhoneNumber } = useFormatting();
    const { getSchemaLabelById } = useSchema();

    const context = useMemo(() => (withCSContext({ queryParams: { memberId } })), [memberId]);

    const {
        loading: isLoading, error, data: dataClientRaw, refetch,
    } = useQuery(CLIENT_INFORMATION, {
        variables: { clientId },
        context,
    });

    const dataClient = useMemo(() => adaptClientInformation(dataClientRaw?.contacts?.items?.[0], {
        t,
        getFormattedDate,
        getFormattedPhoneNumber,
        getSchemaLabelById,
    }), [dataClientRaw, getFormattedDate, getFormattedPhoneNumber, getSchemaLabelById, t]);

    const hasRiskProfile = useMemo(() => dataClient?.HasRiskProfile, [dataClient]);

    return {
        refetch,
        data: dataClient,
        error,
        isLoading,
        hasRiskProfile,
    };
};
